import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { useIsMobile } from 'pricing-pages-lib/js/context/IsMobileContext.tsx';
import { PPUIGrid } from 'pricing-pages-ui-components/grid/PPUIGrid';
import { PPUIGridItem } from 'pricing-pages-ui-components/grid/PPUIGridItem';
import { SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { PRODUCT_CARD } from '../../../constants/tracking/TrackingSources';
import { TalkToSalesFallback } from '../../../features/purchaseMotions/components/TalkToSalesFallback';
import { useBundledProductPurchaseMotions } from '../../../features/purchaseMotions/hooks/useBundledProductPurchaseMotions';
import { useGlobalLoading } from '../../../hooks/loadingHooks';
import { useAppSelector } from '../../../reducers';
import { getIsLatestVersionOfProductInProductInventorySelector, hasHigherProductLevelInProductInventorySelector } from '../../../selectors/ProductInventorySelectors';
import { getIsTBSkuSelector, productSelector } from '../../../selectors/ProductsSelectors';
import { shouldShowTermSelectorForMerchSelector } from '../../../selectors/TermSelectors';
import { useQueryParamTerm } from '../../../utils/urls/appQueryParamUtils';
import ProductIcon from '../../common/ProductIcon';
import { default as DetailedTermSelector } from '../../common/TermSelectors/DetailedTermSelector';
import CurrentPlanTag from '../../common/badges/CurrentPlanTag';
import { default as SkeletonLoadWrapper } from '../../common/loading/SkeletonLoadWrapper';
import ErrorBoundary from '../../error-boundary/ErrorBoundary';
import RequiredOnboardingSubtextContainer from '../../plan-builder/RequiredOnboardingSubtextContainer';
import IncludedSeatsDisplay from '../IncludedSeatsDisplay';
import { SuiteFeatures } from '../features-display/SuiteFeatures';
import { ProductTileFeaturesContainer } from '../features-display/features-display-containers/ProductTileFeaturesContainer';
import ProductTileCTAs from './ProductTileCTAs';
import ProductTilePrice from './ProductTilePrice';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const ProductTileContent = ({
  apiName,
  screen,
  trackingSource = PRODUCT_CARD,
  renderPrimaryBadge,
  renderSecondaryBadge,
  i18nKeyOverride,
  productNameOverride,
  gridItemProps,
  skipInterstitialPage = false
}) => {
  const isAppReady = useGlobalLoading();
  const isMobile = useIsMobile();
  const product = useAppSelector(state => {
    if (!isAppReady) return;
    return productSelector(state, {
      apiName
    });
  });
  const skuId = product === null || product === void 0 ? void 0 : product.currentSkuId;
  const productLine = product === null || product === void 0 ? void 0 : product.productLine;

  // Assert this type to remove BASIC
  const productLevel = product === null || product === void 0 ? void 0 : product.productLevel;
  const isHubspotFree = apiName === HUBSPOT_FREE;
  const hasHigherTieredProduct = useAppSelector(state => {
    if (!productLevel) {
      return false;
    }
    return hasHigherProductLevelInProductInventorySelector(state, {
      productLevel,
      productLine
    });
  });
  const isCurrentPlanLevel = useAppSelector(state => isAppReady && apiName ? getIsLatestVersionOfProductInProductInventorySelector(state, {
    productIdentifier: {
      apiName
    }
  }) : false);
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const shouldShowTermSelector = useAppSelector(state => isAppReady && skuId ? shouldShowTermSelectorForMerchSelector(state, {
    skuId
  }) : false);
  const purchaseMotions = useBundledProductPurchaseMotions({
    skuId
  });
  const isSuiteProduct = useAppSelector(state => isAppReady && skuId ? getIsTBSkuSelector(state, {
    skuId
  }) : false);
  if (!isAppReady || !product || !apiName || !skuId) {
    return null;
  }
  const i18nKey = i18nKeyOverride || `productTile.${apiName}`;
  const productName = productNameOverride || productLine;
  const shouldShowSuiteObjectives = (isHubspotFree || isSuiteProduct) && productLevel;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      "data-selenium-id": `${apiName}-merch-card`,
      paddingBlockStart: "800",
      position: "relative",
      showBorderTop: isMobile,
      gridRowStart: 1,
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [isCurrentPlanLevel ? /*#__PURE__*/_jsx(CurrentPlanTag, {}) : null, renderPrimaryBadge ? renderPrimaryBadge() : null, renderSecondaryBadge ? renderSecondaryBadge() : null, /*#__PURE__*/_jsxs(Flex, {
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: "start",
          gap: "200",
          children: [productName && /*#__PURE__*/_jsx(View, {
            marginBlockStart: isMobile ? '0' : '100',
            children: /*#__PURE__*/_jsx(ProductIcon, {
              iconStyle: {
                responsive: false,
                blockSize: '24px'
              },
              name: productName,
              tier: productLevel
            })
          }), /*#__PURE__*/_jsx(SectionTitle, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${i18nKey}.title`
            })
          })]
        })]
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "0",
      gridRowStart: 2,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${i18nKey}.description`
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 3,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [20, 33, 20],
        widths: [60, 80, 140],
        children: /*#__PURE__*/_jsx(ProductTilePrice, {
          apiName: apiName,
          isHubspotFree: isHubspotFree
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 4,
      children: /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        gap: "400",
        children: /*#__PURE__*/_jsx(IncludedSeatsDisplay, {
          skuId: skuId
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 5,
      children: shouldShowTermSelector ? /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [41],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(DetailedTermSelector, {
          apiName: apiName,
          onChange: setQueryParamTerm,
          activeTerm: queryParamTerm,
          trackingSource: trackingSource
        })
      }) : null
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      gridRowStart: 7,
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [47],
        widths: ['100%'],
        children: /*#__PURE__*/_jsx(ErrorBoundary, {
          fallback: /*#__PURE__*/_jsx(TalkToSalesFallback, {
            apiName: apiName,
            screen: screen,
            trackingSource: trackingSource
          }),
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            justifyContent: "center",
            gap: "200",
            inlineSize: "100%",
            children: /*#__PURE__*/_jsx(ProductTileCTAs, {
              apiName: apiName,
              screen: screen,
              trackingSource: trackingSource,
              skipInterstitialPage: skipInterstitialPage,
              hasHigherTieredProduct: hasHigherTieredProduct,
              isHubspotFree: isHubspotFree,
              purchaseMotions: purchaseMotions
            })
          })
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGrid, Object.assign({}, gridItemProps, {
      gridTemplateRows: "subgrid",
      gridRowStart: 8,
      gridRowEnd: 10,
      paddingBlockStart: "800",
      children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
        isLoading: !isAppReady,
        heights: [47, 47],
        widths: ['100%', '100%'],
        children: shouldShowSuiteObjectives ? /*#__PURE__*/_jsx(SuiteFeatures, {
          productLevel: productLevel,
          loading: !isAppReady,
          isCollapsible: isMobile
        }) : /*#__PURE__*/_jsx(ProductTileFeaturesContainer, {
          apiName: apiName,
          isCollapsible: isMobile
        })
      })
    })), /*#__PURE__*/_jsx(PPUIGridItem, Object.assign({}, gridItemProps, {
      paddingBlockStart: "400",
      paddingBlockEnd: "600",
      gridRowStart: 11,
      showBorderBottom: isMobile,
      children: skuId ? /*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        alignItems: "center",
        children: /*#__PURE__*/_jsx(RequiredOnboardingSubtextContainer, {
          queryParamTerm: queryParamTerm,
          skuIds: [skuId],
          trackingSource: trackingSource,
          includesOnboardingCost: false
        })
      }) : null
    }))]
  });
};
export default ProductTileContent;